<template>
    <div>
        <loading v-show="isLoading"></loading>

        <p v-show="!isLoading">Aktueller Zugriffslevel:
            <span class="font-bold">
                <span v-show="hasPartialPermission">
                    Teilzugriff
                </span>
                <span v-show="hasFullPermission">
                    Vollzugriff
                </span>
                <span v-show="!hasPartialPermission && !hasFullPermission">
                    Kein Zugriff
                </span>
            </span>
        </p>

        <div v-show="admin.is_admin">

            <div v-show="!isLoading && !hasPartialPermission">
                <confirm-dialog v-show="local.is_admin" @confirmed="demoteAdmin">
                    <button-secondary class="mt-4">
                        <template slot="icon"><i class="fa fa-ban"></i></template>
                        Alle Rechte entziehen
                    </button-secondary>
                    <template v-slot:header>Recht entziehen</template>
                    <template v-slot:question>Sollen wirklich alle Adminrechte dem Nutzer entzogen werden?</template>
                    <template v-slot:confirm>
                        <button-primary>
                            <template slot="icon"><i class="fa fa-ban"></i></template>
                            Rechte entziehen
                        </button-primary>
                    </template>
                </confirm-dialog>

                <div class="flex justify-end mt-4">
                    <button-secondary class="mr-2" v-show="!local.is_admin" @click="promoteAdmin"><i
                        class="fa fa-plus mr-2"></i>
                        Vollzugriff
                    </button-secondary>
                    <button-secondary v-show="!local.is_admin" @click="promote"><i class="fa fa-plus mr-2"></i>
                        Teilzugriff
                    </button-secondary>
                </div>
            </div>

            <div v-show="!isLoading && hasPartialPermission">

                <div class="flex justify-end mb-4">
                    <confirm-dialog v-show="hasPartialPermission" @confirmed="revoke">
                        <button-secondary class="mt-4">
                            <template slot="icon"><i class="fa fa-ban"></i></template>
                            Alle Rechte entziehen
                        </button-secondary>
                        <template v-slot:header>Recht entziehen</template>
                        <template v-slot:question>Sollen wirklich alle Adminrechte dem Nutzer entzogen werden?
                        </template>
                        <template v-slot:confirm>
                            <button-primary>
                                <template slot="icon"><i class="fa fa-ban"></i></template>
                                Rechte entziehen
                            </button-primary>
                        </template>
                    </confirm-dialog>
                </div>

                <div class="mb-8">
                    <toggle-group v-model="mayInviteUsers" class="mb-4">
                        Darf Teiladmin Nutzer einladen
                    </toggle-group>
                </div>
                <div class="mb-8">
                    <toggle-group v-model="mayCreateReservations" class="mb-4">
                        Darf Teiladmin Buchungen für Nutzer durchführen und diese sehen
                    </toggle-group>
                    <toggle-group v-model="mayCreateConferenceRoomBookings" class="mb-4">
                        Darf Teiladmin Konferenzräume für Nutzer/Gäste buchen und einsehen
                    </toggle-group>
                    <toggle-group v-if="!mayCreateConferenceRoomBookings" v-model="maySeeConferenceRoomBookings"
                                  class="mb-4">
                        Darf Teiladmin Konferenzräume für Nutzer/Gäste nur einsehen
                    </toggle-group>
                    <toggle-group v-model="mayCreateBookableBookings" class="mb-4">
                        Darf Teiladmin Bookable für Nutzer/Gäste buchen und einsehen
                    </toggle-group>
                    <tailwind-table class="mb-8" :header="['Schreibtischgruppe', 'Zugriff']" v-show="!mayEditAnyDesk">
                        <permission-item
                            v-for="(desk, i) in rooms"
                            v-model="rooms[i]"
                            :key="desk.id"
                        >
                        </permission-item>
                    </tailwind-table>
                </div>

                <div>
                    Mehr Rechte lassen sich im neuen Dashboard verwalten.
                </div>

                <div class="flex justify-end mt-8">
                    <button-primary @click="save"><i class="fa fa-save mr-2"></i> Speichern</button-primary>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import ButtonPrimary from "./UI/ButtonPrimary";
import Modal from "./Modal";
import Loading from "./UI/Loading";
import PermissionItem from "./PermissionItem";
import ButtonWhite from "./UI/ButtonWhite";
import ConfirmDialog from "./UI/ConfirmDialog";
import TailwindTable from "./UI/TailwindTable";
import ButtonSecondary from "./UI/ButtonSecondary";
import ToggleGroup from "./UI/ToggleGroup.vue";

export default {
    name: "PermissionEdit",
    inject: ['admin'],

    components: {
        ToggleGroup,
        ButtonSecondary,
        TailwindTable,
        ConfirmDialog,
        ButtonWhite,
        PermissionItem,
        Loading,
        Modal,
        ButtonPrimary,
    },

    props: ['value'],

    data: () => ({
        loadingCount: 0,
        userGroups: [],
        zones: [],
        rooms: [],
        users: [],
        relation_permission: {},
        mayEditAnyUser: false,
        mayEditAnyDesk: false,
        mayInviteUsers: false,
        mayCreateConferenceRoomBookings: false,
        maySeeConferenceRoomBookings: false,
        mayCreateBookableBookings: false,
        mayCreateReservations: false,
        maySeeAllUsers: false
    }),

    watch: {
        maySeeAllUsers(val) {
            if (val) {
                this.mayEditAnyUser = false;
            }
        },
        mayEditAnyUser(val) {
            if (val) {
                this.maySeeAllUsers = false;
            }
        }
    },

    created() {
        this.load();
    },

    computed: {
        isLoading() {
            return this.loadingCount > 0;
        },
        hasPartialPermission() {
            return this.local.is_sub_admin && this.local.relation_permission;
        },
        hasFullPermission() {
            return this.local.is_admin;
        },
        local: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },
    },

    methods: {
        load() {
            if (!this.hasPartialPermission) {
                return;
            }
            this.loadingCount = 1;
            this.$http.get(`/admin/permissions/${this.local.relation_permission.id}`).then(result => {
                this.loadingCount--;
                this.relation_permission = result.data.data;
                this.mayEditAnyDesk = result.data.data.may_edit_any_desk;
                this.mayEditAnyUser = result.data.data.may_edit_any_user;
                this.maySeeAllUsers = result.data.data.may_see_all_users;
                this.mayCreateReservations = result.data.data.may_create_reservations;
                this.mayInviteUsers = result.data.data.may_invite_users;
                this.mayCreateConferenceRoomBookings = result.data.data.may_manage_conference_room_bookings;
                this.maySeeConferenceRoomBookings = result.data.data.may_see_conference_room_bookings;
                this.mayCreateBookableBookings = result.data.data.may_manage_bookable_bookings;
                this.loadMore();
            })
        },
        loadMore() {
            this.loadingCount = 2;
            this.$http.get('/admin/user_groups').then(result => {
                this.loadingCount--;
                this.userGroups = result.data.data;
                this.mapUser();
            })
            this.$http.get('/admin/zones').then(result => {
                this.loadingCount--;
                this.zones = result.data.data;
                this.mapDesk();
            })
        },
        save() {
            this.loadingCount = 1;
            this.$http.post('/admin/permissions', {
                user: this.local.id,
                user_groups: this.users,
                zones: this.rooms,
                may_edit_any_user: this.mayEditAnyUser,
                may_edit_any_desk: this.mayEditAnyDesk,
                may_see_all_users: this.maySeeAllUsers,
                may_create_reservations: this.mayCreateReservations,
                may_invite_users: this.mayInviteUsers,
                may_manage_conference_room_bookings: this.mayCreateConferenceRoomBookings,
                may_see_conference_room_bookings: this.maySeeConferenceRoomBookings,
                may_manage_bookable_bookings: this.mayCreateBookableBookings
            }).then(result => {
                this.loadingCount--;
                this.local = result.data.user;
                // ensure this.local has the current value!
                this.$root.$nextTick(() => {
                    this.$root.$nextTick(() => {
                        this.load();
                    });
                });
            }).catch(error => {
                this.loadingCount--;
                this.$root.$emit('error', error);
            })
        },
        mapDesk() {
            this.rooms = this.zones.map((desk) => {
                desk.access = 'none';
                const match = this.relation_permission.zones.find((item) => {
                    return item.id === desk.id;
                });
                if (match) {
                    desk.access = match.access;
                }
                return desk;
            });
        },
        mapUser() {
            this.users = this.userGroups.map((group) => {
                group.access = 'none';
                const match = this.relation_permission.user_groups.find((item) => {
                    return item.id === group.id;
                });
                if (match) {
                    group.access = match.access;
                }
                return group;
            });
        },
        promote() {
            this.save();
        },
        promoteAdmin() {
            this.loadingCount = 1;
            this.$http.post(`/admin/permissions`, {
                user: this.local.id,
                is_admin: true,
            }).then(result => {
                this.loadingCount--;
                this.local = result.data.user;
                // ensure this.local has the current value!
                this.$root.$nextTick(() => {
                    this.$root.$nextTick(() => {
                        this.load();
                    });
                });
                this.$root.$emit('alert', 'Berechtigung gespeichert.');
            }).catch(error => {
                this.loadingCount--;
                this.$root.$emit('error', error);
            })
        },
        demoteAdmin() {
            this.loadingCount = 1;
            this.$http.post(`/admin/permissions`, {
                user: this.local.id,
                is_admin: false,
            }).then(result => {
                this.loadingCount--;
                this.local = result.data.user;
                this.load();
            }).catch(error => {
                this.loadingCount--;
                this.$root.$emit('error', error);
            })
        },
        revoke() {
            this.loadingCount = 1;
            this.userGroups = [];
            this.zones = [];
            this.rooms = [];
            this.users = [];
            this.$http.post('/admin/permissions/' + this.local.relation_permission.id, {
                _method: 'delete',
            }).then(result => {
                this.loadingCount--;
                this.local = result.data.user;
                // ensure this.local has the current value!
                this.$root.$nextTick(() => {
                    this.$root.$nextTick(() => {
                        this.load();
                    });
                });
            }).catch(error => {
                this.loadingCount--;
                this.$root.$emit('error', error);
            })
        }
    }
}
</script>

<style scoped>

</style>
